
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { useEffect, useState, useRef } from "react";
import { createLogger } from "@/utils/logger";

const logger = createLogger('LoadingTransition');

export const LoadingTransition = ({ onComplete }: { onComplete: () => void }) => {
  logger.debug("Rendering");
  
  // Add a state to control when the component is shown
  const [isVisible, setIsVisible] = useState(true);
  const onCompleteWasCalled = useRef(false);
  const minDisplayTime = 1800; // Minimum display time in ms
  
  // Use an effect to automatically hide the component after delay
  useEffect(() => {
    logger.debug("Effect running");
    const startTime = Date.now();
    
    // Call onComplete after ensuring minimum display time
    const completeTimer = setTimeout(() => {
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minDisplayTime - elapsedTime);
      
      logger.debug(`Scheduling completion after ${remainingTime}ms remaining time`);
      
      setTimeout(() => {
        if (!onCompleteWasCalled.current) {
          logger.debug("Min display time reached, calling onComplete");
          onCompleteWasCalled.current = true;
          onComplete();
        }
      }, remainingTime);
    }, 500); // Initial assessment after 500ms
    
    // Handle animation separately
    const visibilityTimer = setTimeout(() => {
      logger.debug("Animation timer expired, setting isVisible to false");
      setIsVisible(false);
    }, minDisplayTime - 300); // Start hiding slightly before completion
    
    return () => {
      logger.debug("Cleaning up timers");
      clearTimeout(completeTimer);
      clearTimeout(visibilityTimer);
    };
  }, [onComplete, minDisplayTime]);

  return (
    <AnimatePresence mode="wait">
      {isVisible && (
        <motion.div
          key="loading-screen"
          className="fixed inset-0 flex items-center justify-center z-50"
          style={{ backgroundColor: "#0173dd" }}
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ 
            opacity: 0,
            scale: 1.1, // Add slight scale to ensure it fully leaves the viewport
            transition: {
              duration: 0.5,
              ease: "easeOut"
            }
          }}
          onAnimationComplete={(definition) => {
            // When exit animation completes, ensure we clean up
            if (definition === "exit") {
              logger.debug("Exit animation complete");
              // Safari needs extra help to clean up the background
              document.body.style.backgroundColor = "";
              document.documentElement.style.backgroundColor = "";
            }
          }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex flex-col items-center gap-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <motion.div
                animate={{ 
                  y: [0, -15, 0],
                  transition: {
                    y: {
                      duration: 1.2,
                      ease: "easeInOut",
                      times: [0, 0.5, 1],
                    }
                  }
                }}
              >
                <img 
                  src="/lovable-uploads/1c9d5943-d160-449f-b50c-035b9a83893b.png" 
                  alt="HomePlate Icon" 
                  className="h-20 w-20"
                />
              </motion.div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <img 
                src="/lovable-uploads/00491ec8-6dcb-4232-ac91-3fe849945203.png" 
                alt="HomePlate" 
                className="h-12"
              />
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
